// ============================================
// SOAP Note Parser
//
// Extracts Dr. C's clinical assessment from SOAP
// note PDFs using Claude Vision. Maps generic
// product names to formulary IDs.
// ============================================

import { claudeVision } from '../ai/providers/claude';
import { SKILL_CONFIGS } from '../ai/index';
import { resolveGenericToFormulary } from '../firestore/genericFormularyService';
import type { DrCAssessment } from '../../types/labUpload';

const SOAP_EXTRACTION_PROMPT = `You are extracting a physician's clinical assessment from a SOAP note. Extract the following into a JSON object:

{
  "diagnosis": "The physician's primary diagnosis or clinical impression (string)",
  "products": ["Array of all supplements, medications, or treatments recommended"],
  "labOrders": ["Array of any follow-up lab tests ordered"],
  "notes": "Any additional clinical notes, observations, or instructions"
}

Rules:
- Extract EXACTLY what the physician wrote — do not add clinical interpretation
- For products, use the exact names/descriptions from the SOAP note
- If a section has no content, use an empty string or empty array
- Return ONLY valid JSON, no markdown or explanation`;

/**
 * Parse a SOAP note PDF and extract Dr. C's clinical assessment.
 */
export async function parseSoapNote(
  base64: string,
  mimeType: string,
): Promise<DrCAssessment> {
  const config = SKILL_CONFIGS['lab-analyst'];

  const response = await claudeVision(
    config.model,
    SOAP_EXTRACTION_PROMPT,
    base64,
    mimeType,
    'Extract the clinical assessment from this SOAP note. Return JSON only.',
    0,
    4096,
  );

  // Parse response
  let cleaned = response.trim();
  if (cleaned.startsWith('```')) {
    cleaned = cleaned.replace(/^```(?:json)?\n?/, '').replace(/\n?```$/, '');
  }

  const parsed = JSON.parse(cleaned) as {
    diagnosis: string;
    products: string[];
    labOrders: string[];
    notes: string;
  };

  // Map generic product names to formulary IDs where possible
  const mappedProducts: string[] = [];
  for (const product of parsed.products) {
    const mapping = await resolveGenericToFormulary(product);
    if (mapping) {
      mappedProducts.push(`${product} → ${mapping.formularyProductId}`);
    } else {
      mappedProducts.push(product);
    }
  }

  return {
    diagnosis: parsed.diagnosis || '',
    products: mappedProducts,
    labOrders: parsed.labOrders || [],
    notes: parsed.notes || '',
    extractedFrom: 'soap_pdf',
  };
}
